import { Sentry } from '@/services/sentry'
import { getIosTrackingAuthorization } from '@/services/capacitor.trackingAuthorizationPlugin'

/**
 * Click on the "Decline" / "Accept" button on the Termly consent banner
 * based on the user's response to the iOS tracking authorization request.
 *
 * On android and web, it does nothing.
 */
export async function initConsentBannerOnIOS(): Promise<void> {
  const trackingAuthorizationStatus = await getIosTrackingAuthorization()
  if (trackingAuthorizationStatus === null) {
    // Null here means that the code is not running in the native iOS app,
    // so we just return.
    return
  }

  if (trackingAuthorizationStatus === 'notDetermined') {
    // The user has not answered the tracking authorization prompt.
    // The native app code will trigger an event when that happens.
    document.addEventListener('trackingPermissionChanged', (data: any) => {
      updateTermlyConsent(data.authorized)
    })
  }

  if (['authorized', 'notRequired'].includes(trackingAuthorizationStatus)) {
    updateTermlyConsent(true)
  }

  if (['denied', 'restricted'].includes(trackingAuthorizationStatus)) {
    updateTermlyConsent(false)
  }
}

/**
 * Try to click on the consent button and retries if it fails.
 */
function updateTermlyConsent(consentGranted: boolean): void {
  const foundButton = clickOnConsentButton(consentGranted)
  if (foundButton) {
    return
  }

  // We have no direct contol over Termly's code, so it could be
  // that this function runs before the banner has actually rendered.
  // In that case, we set a mutation observer to keep trying.
  const observer = new MutationObserver((mutations, obs) => {
    const foundButton2 = clickOnConsentButton(consentGranted)
    if (foundButton2) {
      obs.disconnect()
    }
  })
  observer.observe(document.body, { childList: true })

  // Set a timeout to clear the observer, so that we don't have it
  // running in perpetuity if something went wrong.
  setTimeout(() => {
    observer.disconnect()
    Sentry.captureException("Couldn't find the button in the consent banner.")
  }, 5000)
}

/**
 * Tries to click on the consent button.
 *
 * @returns `true` if it suceeded, or `false` if it failed.
 */
function clickOnConsentButton(consentGranted: boolean): boolean {
  if (!window.Termly) {
    Sentry.captureException('Termly failed to load.')
    return true
  }

  const hasConsent = window.Termly.getConsentState()?.advertising === true
  if (hasConsent) {
    // User has already consented, so there's no button to click.
    return true
  }

  const banner = document.querySelector('#termly-code-snippet-support')
  const buttonSelector = consentGranted
    ? 'button[data-tid="banner-accept"]'
    : 'button[data-tid="banner-decline"]'

  const button = banner?.querySelector(buttonSelector) as HTMLElement | null
  if (button) {
    button.click()
    return true
  }

  // Couldn't find the button. Return `false` so we know that we should try it again.
  return false
}
